import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import html2canvas from 'html2canvas';
import { 
  Share2, X, MessageCircle, Instagram, Copy, 
  Download, CheckCircle, Loader2, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

// Share card templates
const CARD_THEMES = {
  prediction: {
    gradient: 'from-[#C84B32] via-[#E85D43] to-[#E8913A]',
    accent: '#D4AF37',
    pattern: 'stars'
  },
  kundli: {
    gradient: 'from-amber-500 via-orange-500 to-amber-600',
    accent: '#FFD700',
    pattern: 'cosmic'
  },
  compatibility: {
    gradient: 'from-pink-500 via-rose-500 to-pink-600',
    accent: '#FF69B4',
    pattern: 'hearts'
  },
  palm: {
    gradient: 'from-green-500 via-emerald-500 to-green-600',
    accent: '#50C878',
    pattern: 'lines'
  }
};

export const ShareCard = ({ 
  type = 'prediction',
  data,
  userName,
  onClose 
}) => {
  const cardRef = useRef(null);
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const theme = CARD_THEMES[type] || CARD_THEMES.prediction;
  const appUrl = window.location.origin;

  const generateShareText = () => {
    let text = '';
    
    switch (type) {
      case 'prediction':
        text = language === 'hi'
          ? `🌟 मेरा आज का राशिफल - Nakshatra\n\n${data?.summary || ''}\n\n🍀 शुभ रंग: ${data?.lucky_color || ''}\n🔢 शुभ अंक: ${data?.lucky_number || ''}\n\n✨ अपना राशिफल देखें: ${appUrl}`
          : `🌟 My Daily Horoscope - Nakshatra\n\n${data?.summary || ''}\n\n🍀 Lucky Color: ${data?.lucky_color || ''}\n🔢 Lucky Number: ${data?.lucky_number || ''}\n\n✨ Check your horoscope: ${appUrl}`;
        break;
      case 'kundli':
        text = language === 'hi'
          ? `🌙 मेरी जन्म कुंडली - Nakshatra\n\n☀️ राशि: ${data?.sun_sign || ''}\n🌙 चंद्र राशि: ${data?.moon_sign || ''}\n⭐ नक्षत्र: ${data?.nakshatra || ''}\n🏠 लग्न: ${data?.lagna || ''}\n\n✨ अपनी कुंडली बनाएं: ${appUrl}`
          : `🌙 My Birth Chart - Nakshatra\n\n☀️ Sun Sign: ${data?.sun_sign || ''}\n🌙 Moon Sign: ${data?.moon_sign || ''}\n⭐ Nakshatra: ${data?.nakshatra || ''}\n🏠 Lagna: ${data?.lagna || ''}\n\n✨ Create your Kundli: ${appUrl}`;
        break;
      case 'compatibility':
        text = language === 'hi'
          ? `💕 कुंडली मिलान परिणाम - Nakshatra\n\n${data?.person1 || ''} & ${data?.person2 || ''}\n💯 अनुकूलता स्कोर: ${data?.score || 0}%\n\n✨ अपनी अनुकूलता जांचें: ${appUrl}`
          : `💕 Kundli Milan Result - Nakshatra\n\n${data?.person1 || ''} & ${data?.person2 || ''}\n💯 Compatibility Score: ${data?.score || 0}%\n\n✨ Check your compatibility: ${appUrl}`;
        break;
      case 'palm':
        text = language === 'hi'
          ? `🖐️ मेरा हस्तरेखा फल - Nakshatra\n\n${data?.summary || ''}\n\n✨ अपनी हस्तरेखा पढ़वाएं: ${appUrl}`
          : `🖐️ My Palm Reading - Nakshatra\n\n${data?.summary || ''}\n\n✨ Get your palm read: ${appUrl}`;
        break;
      default:
        text = `✨ Nakshatra - Vedic Astrology\n\n${appUrl}`;
    }
    
    return text;
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareToInstagram = async () => {
    // Generate image for Instagram story
    await generateImage();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const generateImage = async () => {
    if (!cardRef.current) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `nakshatra-${type}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Image generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const renderCardContent = () => {
    switch (type) {
      case 'prediction':
        return (
          <>
            <h3 className="text-white text-xl font-bold mb-2">
              {language === 'hi' ? 'आज का राशिफल' : "Today's Horoscope"}
            </h3>
            <p className="text-white/90 text-sm mb-4 line-clamp-3">
              {data?.summary || ''}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full" style={{ backgroundColor: data?.lucky_color?.toLowerCase() || '#D4AF37' }} />
                <span className="text-white/80 text-sm">{data?.lucky_color || 'Golden'}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold text-[#C84B32]">{data?.lucky_number || 7}</span>
              </div>
            </div>
          </>
        );
      
      case 'kundli':
        return (
          <>
            <h3 className="text-white text-xl font-bold mb-3">
              {language === 'hi' ? 'जन्म कुंडली' : 'Birth Chart'}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: language === 'hi' ? 'राशि' : 'Sun Sign', value: data?.sun_sign },
                { label: language === 'hi' ? 'चंद्र राशि' : 'Moon Sign', value: data?.moon_sign },
                { label: language === 'hi' ? 'नक्षत्र' : 'Nakshatra', value: data?.nakshatra },
                { label: language === 'hi' ? 'लग्न' : 'Lagna', value: data?.lagna }
              ].map((item, i) => (
                <div key={i} className="bg-[#FCE9D9]/70 rounded-lg p-2 text-center">
                  <p className="text-white/60 text-xs">{item.label}</p>
                  <p className="text-white font-semibold text-sm">{item.value || '-'}</p>
                </div>
              ))}
            </div>
          </>
        );
      
      case 'compatibility':
        return (
          <>
            <h3 className="text-white text-xl font-bold mb-3">
              {language === 'hi' ? 'कुंडली मिलान' : 'Kundli Milan'}
            </h3>
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="text-white font-medium">{data?.person1 || 'Person 1'}</span>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold">{data?.score || 0}%</span>
              </div>
              <span className="text-white font-medium">{data?.person2 || 'Person 2'}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-pink-400 to-rose-400 rounded-full"
                style={{ width: `${data?.score || 0}%` }}
              />
            </div>
          </>
        );
      
      case 'palm':
        return (
          <>
            <h3 className="text-white text-xl font-bold mb-2">
              {language === 'hi' ? 'हस्तरेखा फल' : 'Palm Reading'}
            </h3>
            <p className="text-white/90 text-sm line-clamp-4">
              {data?.summary || data?.lifeLine || ''}
            </p>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`w-full max-w-sm rounded-2xl overflow-hidden ${
            isDark ? 'bg-white' : 'bg-white'
          }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${
            isDark ? 'border-[#282622]/10' : 'border-[#282622]/10'
          }`}>
            <div className="flex items-center gap-2">
              <Share2 className={`w-5 h-5 ${isDark ? 'text-[#C84B32]' : 'text-[#C84B32]'}`} />
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {language === 'hi' ? 'साझा करें' : 'Share'}
              </h3>
            </div>
            <button onClick={onClose} className={`p-1 rounded-full ${isDark ? 'hover:bg-[#FCE9D9]' : 'hover:bg-[#FCE9D9]/60'}`}>
              <X className={`w-5 h-5 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`} />
            </button>
          </div>

          {/* Share Card Preview */}
          <div className="p-4">
            <div 
              ref={cardRef}
              className={`rounded-xl p-5 bg-gradient-to-br ${theme.gradient} relative overflow-hidden`}
              style={{ aspectRatio: '9/16', maxHeight: '280px' }}
            >
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <Sparkles className="absolute top-4 right-4 w-8 h-8 text-white" />
                <Sparkles className="absolute bottom-8 left-4 w-6 h-6 text-white" />
              </div>
              
              {/* Content */}
              <div className="relative z-10 h-full flex flex-col">
                {/* User name */}
                {userName && (
                  <p className="text-white/70 text-xs mb-2">{userName}'s</p>
                )}
                
                {/* Card content */}
                <div className="flex-1">
                  {renderCardContent()}
                </div>
                
                {/* Branding */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#282622]/15">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white font-semibold text-sm">Nakshatra</span>
                  </div>
                  <span className="text-white/60 text-xs">nakshatra.app</span>
                </div>
              </div>
            </div>
          </div>

          {/* Share Options */}
          <div className={`p-4 border-t ${isDark ? 'border-[#282622]/10' : 'border-[#282622]/10'}`}>
            <div className="grid grid-cols-4 gap-3">
              {/* WhatsApp */}
              <button
                onClick={shareToWhatsApp}
                className="flex flex-col items-center gap-1"
              >
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>WhatsApp</span>
              </button>
              
              {/* Instagram (Download for story) */}
              <button
                onClick={shareToInstagram}
                disabled={isGenerating}
                className="flex flex-col items-center gap-1"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                  {isGenerating ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Instagram className="w-6 h-6 text-white" />
                  )}
                </div>
                <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Instagram</span>
              </button>
              
              {/* Copy */}
              <button
                onClick={copyToClipboard}
                className="flex flex-col items-center gap-1"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-[#FCE9D9]/70' : 'bg-[#FCE9D9]/60'
                }`}>
                  {copied ? (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  ) : (
                    <Copy className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#4A4640]'}`} />
                  )}
                </div>
                <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                  {copied ? (language === 'hi' ? 'कॉपी हुआ' : 'Copied') : (language === 'hi' ? 'कॉपी' : 'Copy')}
                </span>
              </button>
              
              {/* Download */}
              <button
                onClick={generateImage}
                disabled={isGenerating}
                className="flex flex-col items-center gap-1"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-[#FCE9D9]/70' : 'bg-[#FCE9D9]/60'
                }`}>
                  {isGenerating ? (
                    <Loader2 className="w-6 h-6 text-[#C84B32] animate-spin" />
                  ) : (
                    <Download className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#4A4640]'}`} />
                  )}
                </div>
                <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                  {language === 'hi' ? 'डाउनलोड' : 'Download'}
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Share button component to use in feature pages
export const ShareButton = ({ 
  type, 
  data, 
  userName,
  className = '' 
}) => {
  const [showShare, setShowShare] = useState(false);
  const { language } = useLanguage();
  const { isDark } = useTheme();

  return (
    <>
      <Button
        onClick={() => setShowShare(true)}
        variant="outline"
        className={`flex items-center gap-2 ${
          isDark ? 'border-[#282622]/15 text-white hover:bg-[#FCE9D9]/60' : 'border-[#282622]/10 text-[#4A4640] hover:bg-[#FFF9EF]'
        } ${className}`}
      >
        <Share2 className="w-4 h-4" />
        {language === 'hi' ? 'साझा करें' : 'Share'}
      </Button>

      {showShare && (
        <ShareCard 
          type={type}
          data={data}
          userName={userName}
          onClose={() => setShowShare(false)}
        />
      )}
    </>
  );
};

export default ShareCard;
